import { useState } from "react";
import axios from "axios";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { constructPaymentData } from "../../../utils/helpers/constructPaymentData";
import { generateEndpoint } from "utils/helpers/generateEndpoint";

const useCreateEhrAccount = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [ehrAccountLoading, setEhrAccountLoading] = useState(false);
  const [ehrAccountError, setEhrAccountError] = useState(null);

  const createPaymentMethod = async (values) => {
    if (!stripe || !elements) {
      throw new Error("Stripe is not properly initialized");
    }

    const cardElement = elements.getElement(CardElement);

    // Check if the card element has data (not empty)
    if (cardElement._empty === false) {
      // CardElement has data, create payment method
      const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${values.shippingFirstName} ${values.shippingLastName}`,
          email: values.email,
          phone: values.shippingPhone || values.billingPhone,
        },
      });

      if (paymentMethodError) {
        setEhrAccountError(paymentMethodError.message);
        throw new Error(paymentMethodError.message);
      }

      return paymentMethod;
    }

    // CardElement is empty, return null (no payment method)
    return null;
  };

  // ADD THIS FUNCTION - it actually charges the card
  const confirmPayments = async (backendResponse, paymentMethod) => {
    // Parse response if it's stringified
    let data = backendResponse;
    if (typeof backendResponse.body === "string") {
      data = JSON.parse(backendResponse.body);
    }

    const errors = [];

    // Confirm subscription payment
    if (data.subscription?.latest_invoice?.payment_intent?.client_secret) {
      const { error } = await stripe.confirmCardPayment(data.subscription.latest_invoice.payment_intent.client_secret, {
        payment_method: paymentMethod.id,
      });

      if (error) {
        errors.push(`Subscription payment failed: ${error.message}`);
      }
    }

    // Confirm add-on payment
    if (data.paymentIntent?.client_secret) {
      const { error } = await stripe.confirmCardPayment(data.paymentIntent.client_secret, { payment_method: paymentMethod.id });

      if (error) {
        errors.push(`Add-on payment failed: ${error.message}`);
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }

    return data;
  };

  const createEHRAccount = async (values, cartItems, cartTotal, stripeCouponObj, discount, originalSubtotal) => {
    setEhrAccountLoading(true);
    setEhrAccountError(null);

    try {
      // Find EHR package item and check if it has optional payment
      const ehrPackage = cartItems.find((item) => item.metadata?.type === "ehr-subscription");
      const userType = ehrPackage?.metadata?.userType;
      const hasOptionalPayment = ehrPackage?.metadata?.isTrial === "true" || ehrPackage?.metadata?.optionalPayment === "true";

      // Create payment method (may be null for optional payment products without payment info)
      const paymentMethod = await createPaymentMethod(values);

      // For products with optional payment, payment method is optional
      if (hasOptionalPayment && !paymentMethod) {
        console.log("Optional payment product without payment method - proceeding without payment method");
      } else if (!hasOptionalPayment && !paymentMethod) {
        throw new Error("Payment method is required for non-optional payment products");
      }

      // Use constructPaymentData helper to format the request consistently
      const paymentData = constructPaymentData(values, cartTotal, stripeCouponObj, discount, originalSubtotal, cartItems);

      // Construct payload for step function with payment data and EHR-specific fields
      const payload = {
        ...paymentData,
        paymentMethodId: paymentMethod?.id || null,
        hasPaymentMethod: !!paymentMethod, // Boolean flag for Lambda to use
        userType: userType,
        ehrPackage: ehrPackage,
        isTrial: hasOptionalPayment,
        trialDays: ehrPackage?.metadata?.trialDays || null,
      };

      // Determine endpoint based on userType
      let endpoint;
      if (userType === "healing center") {
        // ! Update endpoint variable to use generateEndpoint to dynamically switch between dev and prod .env variables
        // endpoint = process.env.REACT_APP_AWS_EHR_API_CREATE_HEALING_CENTER;
        endpoint = generateEndpoint(
          process.env.REACT_APP_AWS_EHR_API_CREATE_HEALING_CENTER,
          process.env.REACT_APP_AWS_PROD_EHR_API_CREATE_HEALING_CENTER,
          "ehr"
        );
      } else if (userType === "facilitator") {
        // ! Update endpoint variable to use generateEndpoint to dynamically switch between dev and prod .env variables
        // endpoint = process.env.REACT_APP_AWS_EHR_API_CREATE_FACILITATOR;
        endpoint = generateEndpoint(
          process.env.REACT_APP_AWS_EHR_API_CREATE_FACILITATOR,
          process.env.REACT_APP_AWS_PROD_EHR_API_CREATE_FACILITATOR,
          "ehr"
        );
      } else {
        throw new Error(`Invalid or missing userType: ${userType}`);
      }

      /*
      LAMBDA IMPLEMENTATION GUIDE for handling hasPaymentMethod flag:
      
      When creating Stripe subscriptions in your Lambda function, use this logic:
      
      const subscriptionParams = {
        customer: customerId,
        items: [{ price: priceId }],
        payment_behavior: 'default_incomplete', // Always use this for flexibility
        trial_period_days: payload.trialDays || 60,
        payment_settings: {
          save_default_payment_method: 'on_subscription'
        },
        trial_settings: {
          end_behavior: {
            missing_payment_method: 'pause' // Options: 'pause', 'cancel', 'create_invoice'
          }
        }
      };

      // ONLY add payment_method if hasPaymentMethod is true
      if (payload.hasPaymentMethod && payload.paymentMethodId) {
        subscriptionParams.default_payment_method = payload.paymentMethodId;
      }

      const subscription = await stripe.subscriptions.create(subscriptionParams);
      
      This prevents the "empty string for payment_method" error by omitting the parameter
      entirely when no payment method is provided for trial subscriptions.
      */

      if (!endpoint) {
        throw new Error(`EHR endpoint not configured in environment variables for userType: ${userType}`);
      }

      const response = await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Only confirm payments if we have a payment method
      if (paymentMethod) {
        const finalResult = await confirmPayments(response.data, paymentMethod);
        return finalResult;
      } else {
        // For optional payment products without payment method, return the response directly
        console.log("Optional payment product without payment method - skipping payment confirmation");
        return response.data;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Error creating EHR account";
      setEhrAccountError(errorMessage);
      return null;
    } finally {
      setEhrAccountLoading(false);
    }
  };

  return {
    createEHRAccount,
    ehrAccountLoading,
    ehrAccountError,
  };
};

export default useCreateEhrAccount;
